import { useEffect, useRef, useState } from 'react'
import Spinner from '#app/components/spinner.tsx'

interface SkeletonImageProps {
	src: string
	alt: string
	aspectRatioPadding?: string
	fallback?: string | React.JSX.Element
}

export function SkeletonImage({
	src,
	alt,
	aspectRatioPadding = '56.25%',
	fallback,
}: SkeletonImageProps) {
	const [isLoaded, setIsLoaded] = useState(false)
	const [hasError, setHasError] = useState(false)
	const imgRef = useRef<HTMLImageElement>(null)

	// if the image was already cached/loaded before handlers attached…
	useEffect(() => {
		const img = imgRef.current
		if (img && img.complete) {
			// naturalWidth === 0 means load error
			if (img.naturalWidth === 0) {
				setHasError(true)
			} else {
				setIsLoaded(true)
			}
		}
	}, [])

	return (
		<div
			className="relative w-full overflow-hidden"
			style={{ paddingTop: aspectRatioPadding }}
		>
			{!isLoaded && !hasError && (
				<div className="absolute inset-0 animate-pulse bg-gray-200">
					<Spinner />
				</div>
			)}

			{hasError && (
				<div className="absolute inset-0 flex items-center justify-center bg-gray-200">
					{typeof fallback === 'string' ? (
						<img
							src={fallback}
							alt="fallback"
							className="h-12 w-12 object-contain"
						/>
					) : (
						(fallback ?? <span className="text-sm text-gray-500">{alt}</span>)
					)}
				</div>
			)}

			{!hasError && (
				<img
					ref={imgRef}
					className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
						isLoaded ? 'opacity-100' : 'opacity-0'
					}`}
					src={src}
					alt={alt}
					loading="lazy"
					onLoad={() => setIsLoaded(true)}
					onError={() => setHasError(true)}
				/>
			)}
		</div>
	)
}
